import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../../services/api';
import { AuthContext } from '../../../context/AuthContext';
import { Edit2, Check, X, Calendar, Users, BarChart2, Home, Sparkles, CheckSquare, Square } from 'lucide-react';
import RequestHistoryModal from '../../../components/RequestHistoryModal';
import RequestDetailsModal from '../../../components/RequestDetailsModal';
import BulkGrantModal from '../../../components/BulkGrantModal';

const EditableTimeCell = ({ record, field, hasEditAccess, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState('');

  const formatTimeForDisplay = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatTimeForInput = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const handleEditClick = () => {
    setTempValue(formatTimeForInput(record[field]));
    setIsEditing(true);
  };

  const handleSave = () => {
    if (tempValue) {
      onUpdate(record, field, tempValue);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  if (!hasEditAccess) {
    return <span>{formatTimeForDisplay(record[field])}</span>;
  }

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <input
          type="time"
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          className="text-xs border border-gray-300 rounded p-1"
          autoFocus
        />
        <button onClick={handleSave} className="text-green-600 hover:text-green-800" title="Save">
          <Check size={16} />
        </button>
        <button onClick={handleCancel} className="text-red-600 hover:text-red-800" title="Cancel">
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 group">
      <span className="text-gray-700">{formatTimeForDisplay(record[field])}</span>
      <button 
        onClick={handleEditClick}
        className="opacity-0 group-hover:opacity-100 text-blue-500 hover:text-blue-700 transition-opacity p-1"
        title="Edit Time"
      >
        <Edit2 size={14} />
      </button>
    </div>
  );
};

const AdminAttendance = () => {
  const [history, setHistory] = useState([]);
  const [wfhRequests, setWfhRequests] = useState([]);
  const [odRequests, setOdRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wfhLoading, setWfhLoading] = useState(true);
  const [odLoading, setOdLoading] = useState(true);
  const [holidaysLoading, setHolidaysLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') ? searchParams.get('tab').toUpperCase() : 'ATTENDANCE';
  const [activeTab, setActiveTab] = useState(['ATTENDANCE', 'WFH', 'OD', 'HOLIDAYS'].includes(initialTab) ? initialTab : 'ATTENDANCE');
  const [selectedReport, setSelectedReport] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().split('T')[0]);
  const [typeFilter, setTypeFilter] = useState('All Types');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [viewMode, setViewMode] = useState('DAILY'); // 'DAILY' or 'MONTHLY'
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('ALL');
  const [employeeDetailModal, setEmployeeDetailModal] = useState(null); // stores employee object for drill-down modal
  const { user } = useContext(AuthContext);
  const hasEditAccess = user?.role === 'Super Admin';
  // Super Admin, Admin, and HR have permission to change employee attendance status
  const canChangeAttendanceStatus = user?.role === 'Super Admin' || user?.role === 'Admin' || user?.role === 'HR';
  
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [currentHistory, setCurrentHistory] = useState([]);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await api.get('/attendance');
      setHistory(res.data);
    } catch (err) {
      setError('Failed to fetch attendance records');
    } finally {
      setLoading(false);
    }
  };

  const fetchWfhRequests = async () => {
    setWfhLoading(true);
    try {
      const res = await api.get('/attendance/wfh');
      setWfhRequests(res.data);
    } catch (err) {
      setError('Failed to fetch WFH requests');
    } finally {
      setWfhLoading(false);
    }
  };

  const fetchOdRequests = async () => {
    setOdLoading(true);
    try {
      const res = await api.get('/attendance/od');
      setOdRequests(res.data);
    } catch (err) {
      setError('Failed to fetch OD requests');
    } finally {
      setOdLoading(false);
    }
  };

  const fetchHolidays = async () => {
    setHolidaysLoading(true);
    try {
      const res = await api.get(`/holidays?t=${Date.now()}`);
      setHolidays(res.data);
    } catch (err) {
      setError('Failed to fetch holidays');
    } finally {
      setHolidaysLoading(false);
    }
  };

  const fetchLeaveRequests = async () => {
    try {
      const res = await api.get('/leaves');
      setLeaveRequests(res.data || []);
    } catch (err) {
      console.error('Failed to fetch leaves:', err);
    }
  };

  useEffect(() => {
    fetchHistory();
    fetchWfhRequests();
    fetchOdRequests();
    fetchHolidays();
    fetchLeaveRequests();
  }, []);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const upper = tabParam.toUpperCase();
      if (['ATTENDANCE', 'WFH', 'OD', 'HOLIDAYS'].includes(upper)) {
        setActiveTab(upper);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (activeTab === 'HOLIDAYS') {
      fetchHolidays();
    }
  }, [activeTab]);

  const handleUpdateStatus = async (id, newStatus, extraData = {}) => {
    try {
      await api.put(`/attendance/${id}`, { status: newStatus, ...extraData });
      await fetchHistory();
    } catch (err) {
      alert('Failed to update status');
    }
  };

  const handleUpdateTime = async (record, field, timeValue) => {
    try {
      if (!timeValue) return;
      const [hours, minutes] = timeValue.split(':');
      const baseDate = new Date(record[field] || record.date);
      baseDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
      
      await api.put(`/attendance/${record._id}`, { [field]: baseDate.toISOString() });
      await fetchHistory();
    } catch (err) {
      alert(`Failed to update ${field}`);
    }
  };

  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  const formatDuration = (totalMinutes) => {
    if (!totalMinutes || totalMinutes <= 0) return '-';
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  };

  const formatWorkingHours = (record) => {
    if (!record) return <span className="text-gray-400">-</span>;

    // 1. Stored workingHours from clock out
    if (record.workingHours && record.workingHours > 0) {
      const totalMinutes = Math.round(record.workingHours * 60);
      const h = Math.floor(totalMinutes / 60);
      const m = totalMinutes % 60;
      const formatted = h > 0 && m > 0 ? `${h}h ${m}m` : h > 0 ? `${h}h` : `${m}m`;
      return (
        <span className="font-semibold text-gray-900" title={`${record.workingHours.toFixed(2)} hrs`}>
          {formatted}
        </span>
      );
    }

    // 2. Both clockIn and clockOut exist
    if (record.clockIn && record.clockOut) {
      const diffMs = new Date(record.clockOut).getTime() - new Date(record.clockIn).getTime();
      if (diffMs > 0) {
        const totalMinutes = Math.round(diffMs / 60000);
        const h = Math.floor(totalMinutes / 60);
        const m = totalMinutes % 60;
        const formatted = h > 0 && m > 0 ? `${h}h ${m}m` : h > 0 ? `${h}h` : `${m}m`;
        return (
          <span className="font-semibold text-gray-900" title={`${(diffMs / 3600000).toFixed(2)} hrs`}>
            {formatted}
          </span>
        );
      }
    }

    // 3. Shift in progress
    if (record.clockIn && !record.clockOut && record.status === 'CLOCKED_IN') {
      const elapsedMs = Math.max(0, currentTime - new Date(record.clockIn).getTime());
      const totalMinutes = Math.floor(elapsedMs / 60000);
      const h = Math.floor(totalMinutes / 60);
      const m = totalMinutes % 60;
      const formatted = h > 0 && m > 0 ? `${h}h ${m}m` : h > 0 ? `${h}h` : `${m}m`;
      return (
        <span className="inline-flex items-center gap-1.5 text-blue-600 font-semibold" title="Shift in progress">
          <span>{formatted}</span>
          <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse inline-block" />
        </span>
      );
    }

    return <span className="text-gray-400">-</span>;
  };

  const [bulkGrantModalOpen, setBulkGrantModalOpen] = useState(false);
  const [bulkGrantType, setBulkGrantType] = useState('WFH');
  const [selectedWfhIds, setSelectedWfhIds] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const handleWfhUpdateStatus = async (id, newStatus) => {
    try {
      await api.put(`/attendance/wfh/${id}/status`, { status: newStatus });
      await fetchWfhRequests();
    } catch (err) {
      alert('Failed to update WFH status');
    }
  };

  const handleBulkUpdateWfhStatus = async (status) => {
    if (selectedWfhIds.length === 0) {
      alert('Please select at least one WFH request.');
      return;
    }
    if (!window.confirm(`Are you sure you want to mark ${selectedWfhIds.length} request(s) as ${status}?`)) {
      return;
    }

    setBulkProcessing(true);
    try {
      await api.put('/attendance/wfh/bulk-status', {
        requestIds: selectedWfhIds,
        status
      });
      setSelectedWfhIds([]);
      await fetchWfhRequests();
      alert(`Successfully ${status.toLowerCase()}ed ${selectedWfhIds.length} WFH request(s)!`);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update WFH requests');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleOdUpdateStatus = async (id, newStatus) => {
    try {
      await api.put(`/attendance/od/${id}/status`, { status: newStatus });
      await fetchOdRequests();
    } catch (err) {
      alert('Failed to update OD status');
    }
  };

  // Extract unique employees from history
  const uniqueEmployees = React.useMemo(() => {
    const map = new Map();
    history.forEach(rec => {
      if (rec.employeeId?._id && !map.has(rec.employeeId._id.toString())) {
        map.set(rec.employeeId._id.toString(), {
          _id: rec.employeeId._id.toString(),
          name: `${rec.employeeId.firstName || ''} ${rec.employeeId.lastName || ''}`.trim(),
          employeeId: rec.employeeId.employeeId,
          email: rec.employeeId.email,
          role: rec.employeeId.role,
          workLocation: rec.employeeId.workLocation,
          dateOfJoining: rec.employeeId.dateOfJoining,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [history]);

  // Helper to check if a date (YYYY-MM-DD) falls within a WFH request range
  const isDateWithinRange = (dateStr, startDate, endDate) => {
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    return d >= start && d <= end;
  };

  // Aggregate monthly attendance statistics per employee for the selectedMonth (YYYY-MM)
  const monthlySummaryData = React.useMemo(() => {
    if (!selectedMonth) return [];
    
    // Group attendance records by employeeId for this month
    const empMap = new Map();
    
    // Seed with all employees or unique employees found
    uniqueEmployees.forEach(emp => {
      empMap.set(emp._id, {
        employee: emp,
        records: [],
        presentCount: 0,
        halfDayCount: 0,
        absentCount: 0,
        leaveCount: 0,
        paidLeaveCount: 0,
        unpaidLeaveCount: 0,
        holidayCount: 0,
        totalWorkingMinutes: 0,
        earlyLeavingCount: 0,
        overtimeMinutes: 0,
      });
    });

    history.forEach(rec => {
      if (!rec.employeeId?._id) return;
      const empId = rec.employeeId._id.toString();
      const recDate = new Date(rec.date);
      const recMonth = `${recDate.getFullYear()}-${String(recDate.getMonth() + 1).padStart(2, '0')}`;
      
      if (recMonth !== selectedMonth) return;
      
      if (!empMap.has(empId)) {
        empMap.set(empId, {
          employee: {
            _id: empId,
            name: `${rec.employeeId.firstName || ''} ${rec.employeeId.lastName || ''}`.trim(),
            employeeId: rec.employeeId.employeeId,
            email: rec.employeeId.email,
            role: rec.employeeId.role,
            workLocation: rec.employeeId.workLocation,
          },
          records: [],
          presentCount: 0,
          halfDayCount: 0,
          absentCount: 0,
          leaveCount: 0,
          paidLeaveCount: 0,
          unpaidLeaveCount: 0,
          holidayCount: 0,
          totalWorkingMinutes: 0,
          earlyLeavingCount: 0,
          overtimeMinutes: 0,
        });
      }

      const item = empMap.get(empId);
      item.records.push(rec);

      const status = rec.status;
      if (status === 'PRESENT' || status === 'CLOCKED_IN') {
        item.presentCount += 1;
      } else if (status === 'HALF_DAY') {
        item.halfDayCount += 1;
      } else if (status === 'ABSENT') {
        item.absentCount += 1;
      } else if (status === 'LEAVE') {
        item.leaveCount += 1;
        // Check if there's an approved leave request for this record's date to determine paid vs unpaid
        const matchedLeave = leaveRequests.find(l => {
          const lEmpId = l.employeeId?._id ? l.employeeId._id.toString() : (l.employeeId ? l.employeeId.toString() : null);
          if (lEmpId !== empId) return false;
          if (l.status !== 'APPROVED') return false;
          return isDateWithinRange(rec.date, l.startDate, l.endDate);
        });

        if (matchedLeave) {
          const isBothApproved = (matchedLeave.managerStatus === 'APPROVED' && (matchedLeave.adminStatus === 'APPROVED' || matchedLeave.hrStatus === 'APPROVED'));
          const isAdminApproved = matchedLeave.adminStatus === 'APPROVED';

          // If approved by both manager and admin, override HR status completely and treat as full day paid (never unpaid)
          // Also treat as paid if matchedLeave has paidDays > 0, isPaid true, or is Casual Leave
          const isPaid = (isBothApproved || isAdminApproved) ? true : ((matchedLeave.paidDays > 0) || (matchedLeave.leaveType?.isPaid) || (matchedLeave.leaveType?.name === 'Casual Leave'));
          rec.isPaidLeave = isPaid;
          rec.leaveTypeName = matchedLeave.leaveType?.name || 'Leave';
          if (isPaid) {
            item.paidLeaveCount += 1;
          } else {
            item.unpaidLeaveCount += 1;
          }
        } else {
          // Default fallback if no specific request found
          rec.isPaidLeave = true;
          item.paidLeaveCount += 1;
        }
      } else if (status === 'WFH') {
        // WFH rule:
        // 1. WFH employees -> full day, count in Present
        // 2. In-office employees:
        //    - Approved by both manager and admin (or paymentStatus === 'FULL_DAY_PAID') -> count in Present (full day paid)
        //    - Approved only by manager (paymentStatus === 'HALF_DAY_PAID' or managerStatus === 'APPROVED' && adminStatus !== 'APPROVED') -> count in Half Day
        const empWorkLocation = rec.employeeId?.workLocation || item.employee?.workLocation;
        if (empWorkLocation === 'WFH') {
          item.presentCount += 1;
        } else {
          // Look for matching approved WFH request covering this record's date
          const matchedWfhReq = wfhRequests.find(req => {
            const reqEmpId = req.employeeId?._id ? req.employeeId._id.toString() : (req.employeeId ? req.employeeId.toString() : null);
            if (reqEmpId !== empId) return false;
            return isDateWithinRange(rec.date, req.startDate, req.endDate);
          });

          if (matchedWfhReq) {
            const isBothApproved = matchedWfhReq.paymentStatus === 'FULL_DAY_PAID' || 
              (matchedWfhReq.managerStatus === 'APPROVED' && matchedWfhReq.adminStatus === 'APPROVED') ||
              matchedWfhReq.adminStatus === 'APPROVED';
            
            const isManagerOnly = matchedWfhReq.paymentStatus === 'HALF_DAY_PAID' ||
              (matchedWfhReq.managerStatus === 'APPROVED' && matchedWfhReq.adminStatus !== 'APPROVED');

            if (isBothApproved) {
              item.presentCount += 1;
            } else if (isManagerOnly) {
              item.halfDayCount += 1;
            } else {
              item.presentCount += 1; // Fallback for approved WFH
            }
          } else {
            // Default fallback if no specific request document found
            item.presentCount += 1;
          }
        }
      } else if (status === 'HOLIDAY') {
        item.holidayCount += 1;
      }

      if (rec.workingHours) {
        item.totalWorkingMinutes += Math.round(rec.workingHours * 60);
      } else if (rec.clockIn && rec.clockOut) {
        const diff = Math.max(0, new Date(rec.clockOut).getTime() - new Date(rec.clockIn).getTime());
        item.totalWorkingMinutes += Math.round(diff / 60000);
      }

      if (rec.earlyLeavingMinutes > 0) item.earlyLeavingCount += 1;
      if (rec.overtimeMinutes > 0) item.overtimeMinutes += rec.overtimeMinutes;
    });

    // Populate missing absent working days for each employee in the selected month
    const [yearStr, monthStr] = selectedMonth.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10); // 1-indexed
    const daysInMonth = new Date(year, month, 0).getDate();
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    empMap.forEach((item, empId) => {
      // Map of existing record dates for quick lookup (YYYY-MM-DD)
      const existingDateMap = new Set();
      item.records.forEach(r => {
        const d = new Date(r.date);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        existingDateMap.add(`${y}-${m}-${day}`);
      });

      const joiningDate = item.employee?.dateOfJoining ? new Date(item.employee.dateOfJoining) : null;
      if (joiningDate) joiningDate.setHours(0, 0, 0, 0);

      for (let dayNum = 1; dayNum <= daysInMonth; dayNum++) {
        const currentDate = new Date(year, month - 1, dayNum);
        currentDate.setHours(0, 0, 0, 0);

        // Only evaluate days up to today
        if (currentDate > today) break;

        // If employee joined after this date, skip
        if (joiningDate && currentDate < joiningDate) continue;

        // Skip Sundays (weekly off)
        if (currentDate.getDay() === 0) continue;

        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;

        // Check if day is an official company holiday
        const isHoliday = holidays.some(h => {
          const hd = new Date(h.date);
          const hy = hd.getFullYear();
          const hm = String(hd.getMonth() + 1).padStart(2, '0');
          const hday = String(hd.getDate()).padStart(2, '0');
          return `${hy}-${hm}-${hday}` === dateStr;
        });
        if (isHoliday) continue;

        // Check if employee has an approved leave covering this day
        const matchedApprovedLeave = leaveRequests.find(l => {
          const lEmpId = l.employeeId?._id ? l.employeeId._id.toString() : (l.employeeId ? l.employeeId.toString() : null);
          if (lEmpId !== empId) return false;
          const isBoth = (l.managerStatus === 'APPROVED' && (l.adminStatus === 'APPROVED' || l.hrStatus === 'APPROVED'));
          const isAdmin = l.adminStatus === 'APPROVED';
          if (l.status !== 'APPROVED' && !isBoth && !isAdmin) return false;
          return isDateWithinRange(dateStr, l.startDate, l.endDate);
        });

        if (matchedApprovedLeave) {
          // If no existing record exists for this approved leave, count in leaveCount
          if (!existingDateMap.has(dateStr)) {
            existingDateMap.add(dateStr);
            item.leaveCount += 1;
            const isBoth = (matchedApprovedLeave.managerStatus === 'APPROVED' && (matchedApprovedLeave.adminStatus === 'APPROVED' || matchedApprovedLeave.hrStatus === 'APPROVED'));
            const isAdmin = matchedApprovedLeave.adminStatus === 'APPROVED';

            // Manager + Admin approval overrides HR status and is guaranteed full day paid
            // Also treat as paid if matchedApprovedLeave has paidDays > 0, isPaid true, or is Casual Leave
            const isPaid = (isBoth || isAdmin) ? true : ((matchedApprovedLeave.paidDays > 0) || (matchedApprovedLeave.leaveType?.isPaid) || (matchedApprovedLeave.leaveType?.name === 'Casual Leave'));
            if (isPaid) {
              item.paidLeaveCount += 1;
            } else {
              item.unpaidLeaveCount += 1;
            }

            item.records.push({
              _id: `synthetic-leave-${empId}-${dateStr}`,
              employeeId: item.employee,
              date: currentDate.toISOString(),
              status: 'LEAVE',
              isPaidLeave: isPaid,
              leaveTypeName: matchedApprovedLeave.leaveType?.name || 'Leave',
              attendanceType: item.employee?.workLocation || 'OFFICE',
              clockIn: null,
              clockOut: null,
              workingHours: 0,
              isSynthetic: true
            });
          }
          continue;
        }

        // Check if day already has an attendance record
        if (!existingDateMap.has(dateStr)) {
          // Day was a working day, employee didn't clock in and had no approved leave -> ABSENT
          item.absentCount += 1;
          existingDateMap.add(dateStr);
          item.records.push({
            _id: `synthetic-absent-${empId}-${dateStr}`,
            employeeId: item.employee,
            date: currentDate.toISOString(),
            status: 'ABSENT',
            attendanceType: item.employee?.workLocation || 'OFFICE',
            clockIn: null,
            clockOut: null,
            workingHours: 0,
            isSynthetic: true
          });
        }
      }
    });

    let list = Array.from(empMap.values());

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter(item => 
        item.employee.name.toLowerCase().includes(term) ||
        item.employee.employeeId?.toLowerCase().includes(term) ||
        item.employee.email?.toLowerCase().includes(term)
      );
    }

    // Filter by selected employee dropdown
    if (selectedEmployeeId !== 'ALL') {
      list = list.filter(item => item.employee._id === selectedEmployeeId);
    }

    return list;
  }, [history, selectedMonth, uniqueEmployees, searchTerm, selectedEmployeeId, wfhRequests, holidays, leaveRequests]);

  return (
    <div className="space-y-6">
      <RequestHistoryModal 
        isOpen={historyModalOpen} 
        onClose={() => setHistoryModalOpen(false)} 
        history={currentHistory} 
      />
      <RequestDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        request={selectedRequest}
        type={activeTab}
      />
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-6 rounded-2xl shadow-sm border border-gray-100 gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Company Attendance</h2>
          <p className="text-sm text-gray-500 mt-1">Review and manage daily attendance records for all employees.</p>
        </div>
        <div className="flex bg-gray-100 p-1 rounded-lg overflow-x-auto w-full md:w-auto hide-scrollbar">
          <button
            onClick={() => setActiveTab('ATTENDANCE')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap shrink-0 ${activeTab === 'ATTENDANCE' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Attendance Logs
          </button>
          <button
            onClick={() => setActiveTab('WFH')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap shrink-0 ${activeTab === 'WFH' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            WFH Approvals
            {wfhRequests.filter(req => req.status === 'PENDING').length > 0 && (
              <span className="flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
                {wfhRequests.filter(req => req.status === 'PENDING').length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('OD')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap shrink-0 ${activeTab === 'OD' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            OD & Meeting Approvals
            {odRequests.filter(req => req.status === 'PENDING').length > 0 && (
              <span className="flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
                {odRequests.filter(req => req.status === 'PENDING').length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('HOLIDAYS')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap shrink-0 ${activeTab === 'HOLIDAYS' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Holidays
          </button>
        </div>
        
        <button
          type="button"
          onClick={() => {
            setBulkGrantType('WFH');
            setBulkGrantModalOpen(true);
          }}
          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all hover:shadow-md shrink-0 w-full md:w-auto justify-center"
        >
          <Sparkles className="w-4 h-4 text-amber-300" />
          <span>Bulk Grant WFH / Leave</span>
        </button>
      </div>

      <BulkGrantModal
        isOpen={bulkGrantModalOpen}
        defaultType={bulkGrantType}
        onClose={() => setBulkGrantModalOpen(false)}
        onSuccess={() => {
          fetchHistory();
          fetchWfhRequests();
          fetchLeaveRequests();
        }}
      />

      {activeTab === 'ATTENDANCE' && (
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-3 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-2">View Mode:</span>
            <div className="inline-flex rounded-xl bg-gray-100 p-1">
              <button
                type="button"
                onClick={() => setViewMode('DAILY')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  viewMode === 'DAILY'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                <Calendar className="w-3.5 h-3.5" />
                Daily Logs
              </button>
              <button
                type="button"
                onClick={() => setViewMode('MONTHLY')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  viewMode === 'MONTHLY'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                <BarChart2 className="w-3.5 h-3.5" />
                Monthly Employee Summary
              </button>
            </div>
          </div>

          {viewMode === 'MONTHLY' && (
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-xl px-2.5 py-1">
                <Calendar className="w-4 h-4 text-gray-500" />
                <label className="text-xs font-medium text-gray-600">Month:</label>
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="bg-transparent border-0 text-xs font-bold text-gray-800 focus:ring-0 p-1 cursor-pointer"
                />
              </div>

              <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-xl px-2.5 py-1">
                <Users className="w-4 h-4 text-gray-500" />
                <label className="text-xs font-medium text-gray-600">Employee:</label>
                <select
                  value={selectedEmployeeId}
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                  className="bg-transparent border-0 text-xs font-medium text-gray-800 focus:ring-0 p-1 cursor-pointer max-w-[200px]"
                >
                  <option value="ALL">All Employees ({uniqueEmployees.length})</option>
                  {uniqueEmployees.map(emp => (
                    <option key={emp._id} value={emp._id}>
                      {emp.name} ({emp.employeeId || emp.role})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 grid grid-cols-1 sm:grid-cols-2 md:flex md:flex-row gap-4 items-stretch md:items-center">
        <input
          type="text"
          placeholder={viewMode === 'MONTHLY' && activeTab === 'ATTENDANCE' ? "Search employee name, ID or email..." : "Search by name, ID, or status..."}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500 text-sm flex-1 min-w-[200px]"
        />
        {activeTab === 'ATTENDANCE' && viewMode === 'DAILY' && (
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        )}
        {activeTab === 'ATTENDANCE' && viewMode === 'DAILY' && (
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500 text-sm"
          >
            <option value="All Types">All Types</option>
            <option value="OFFICE">OFFICE</option>
            <option value="WFH">WFH</option>
            <option value="MEETING">MEETING</option>
          </select>
        )}
        {(activeTab !== 'ATTENDANCE' || viewMode === 'DAILY') && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500 text-sm"
          >
            <option value="All Status">All Status</option>
            {activeTab === 'ATTENDANCE' ? (
              <>
                <option value="PRESENT">PRESENT</option>
                <option value="ABSENT">ABSENT</option>
                <option value="HALF_DAY">HALF DAY</option>
                <option value="CLOCKED_IN">CLOCKED IN</option>
                <option value="PENDING_APPROVAL">PENDING APPROVAL</option>
                <option value="LEAVE">LEAVE</option>
              </>
            ) : activeTab === 'HOLIDAYS' ? null : (
              <>
                <option value="PENDING">PENDING</option>
                <option value="APPROVED">APPROVED</option>
                <option value="REJECTED">REJECTED</option>
              </>
            )}
          </select>
        )}
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 text-red-700 rounded-r-lg shadow-sm">
          {error}
        </div>
      )}

      {activeTab === 'ATTENDANCE' ? (
        viewMode === 'MONTHLY' ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 bg-gray-50/80 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h3 className="text-sm font-bold text-gray-800">
                  Monthly Attendance Summary — {new Date(`${selectedMonth}-01`).toLocaleString('default', { month: 'long', year: 'numeric' })}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Aggregated attendance counts and working hours for each employee. Click on any employee to view their full monthly daily log.
                </p>
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full border border-blue-100 self-start sm:self-auto">
                {monthlySummaryData.length} Employee{monthlySummaryData.length !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50/80">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Present</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Half Day</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Absent</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Leave</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Early Days</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Hours</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {loading ? (
                    <tr><td colSpan="8" className="px-6 py-8 text-center text-gray-500">Loading attendance data...</td></tr>
                  ) : monthlySummaryData.length === 0 ? (
                    <tr><td colSpan="8" className="px-6 py-8 text-center text-gray-500">No attendance records found for this month.</td></tr>
                  ) : (
                    monthlySummaryData.map((item) => (
                      <tr key={item.employee._id} className="hover:bg-gray-50/80 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">{item.employee.name}</div>
                          <div className="text-xs text-gray-500">{item.employee.employeeId || 'N/A'} • {item.employee.role}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800">
                            {item.presentCount}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          <span className={`inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-bold ${
                            item.halfDayCount > 0 ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {item.halfDayCount}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          <span className={`inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-bold ${
                            item.absentCount > 0 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {item.absentCount}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          {item.leaveCount > 0 ? (
                            <div className="inline-flex flex-col items-center gap-1">
                              <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800">
                                {item.leaveCount}
                              </span>
                              <div className="flex items-center gap-1 text-[10px]">
                                {item.paidLeaveCount > 0 && (
                                  <span className="px-1.5 py-0.2 rounded font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200" title="Approved Paid Leaves">
                                    {item.paidLeaveCount}P
                                  </span>
                                )}
                                {item.unpaidLeaveCount > 0 && (
                                  <span className="px-1.5 py-0.2 rounded font-semibold bg-rose-50 text-rose-700 border border-rose-200" title="Approved Unpaid Leaves">
                                    {item.unpaidLeaveCount}U
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-500">
                              0
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          <span className={`inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-bold ${
                            item.earlyLeavingCount > 0 ? 'bg-rose-100 text-rose-800' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {item.earlyLeavingCount}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                          {formatDuration(item.totalWorkingMinutes)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <button
                            type="button"
                            onClick={() => setEmployeeDetailModal(item)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-colors shadow-sm"
                          >
                            <Calendar className="w-3.5 h-3.5" />
                            View Month Log
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50/80">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clock In</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clock Out</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Working Hours</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Late</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Early</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Overtime</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Work Report</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Photos</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan="12" className="px-6 py-4 text-center">Loading...</td></tr>
              ) : history.filter(record => {
                  const term = searchTerm.toLowerCase();
                  const matchSearch = record.employeeId?.firstName?.toLowerCase().includes(term) ||
                    record.employeeId?.lastName?.toLowerCase().includes(term) ||
                    record.employeeId?.employeeId?.toLowerCase().includes(term) ||
                    record.status?.toLowerCase().includes(term);
                  
                  const matchType = typeFilter === 'All Types' || record.attendanceType === typeFilter;
                  const matchStatus = statusFilter === 'All Status' || record.status === statusFilter;
                  
                  let matchDate = true;
                  if (dateFilter) {
                    const recordDate = new Date(record.date);
                    const localDateStr = `${recordDate.getFullYear()}-${String(recordDate.getMonth() + 1).padStart(2, '0')}-${String(recordDate.getDate()).padStart(2, '0')}`;
                    const utcDateStr = record.date ? (typeof record.date === 'string' ? record.date : recordDate.toISOString()).substring(0, 10) : '';
                    matchDate = localDateStr === dateFilter || utcDateStr === dateFilter;
                  }
                  
                  return matchSearch && matchType && matchStatus && matchDate;
                }).length === 0 ? (
                  (() => {
                    if (dateFilter) {
                      const selectedDate = new Date(dateFilter);
                      const isSunday = selectedDate.getDay() === 0;
                      const holiday = holidays.find(h => {
                        const hDate = new Date(h.date);
                        const localHDateStr = `${hDate.getFullYear()}-${String(hDate.getMonth() + 1).padStart(2, '0')}-${String(hDate.getDate()).padStart(2, '0')}`;
                        const utcHDateStr = h.date ? (typeof h.date === 'string' ? h.date : hDate.toISOString()).substring(0, 10) : '';
                        return localHDateStr === dateFilter || utcHDateStr === dateFilter;
                      });
                      if (holiday) {
                        return (
                          <tr>
                            <td colSpan="12" className="px-6 py-16 text-center">
                              <div className="flex flex-col items-center justify-center space-y-2">
                                <span className="text-3xl">🎉</span>
                                <span className="text-purple-400 font-medium text-lg">This day is a company holiday: {holiday.name}</span>
                              </div>
                            </td>
                          </tr>
                        );
                      }
                      if (isSunday) {
                        return (
                          <tr>
                            <td colSpan="12" className="px-6 py-16 text-center">
                              <div className="flex flex-col items-center justify-center space-y-2">
                                <span className="text-3xl">🌅</span>
                                <span className="text-blue-400 font-medium text-lg">This day is a Sunday (Weekly Off)</span>
                              </div>
                            </td>
                          </tr>
                        );
                      }
                    }
                    return <tr><td colSpan="12" className="px-6 py-4 text-center text-gray-500">No records found</td></tr>;
                  })()
              ) : (
                history.filter(record => {
                  const term = searchTerm.toLowerCase();
                  const matchSearch = record.employeeId?.firstName?.toLowerCase().includes(term) ||
                    record.employeeId?.lastName?.toLowerCase().includes(term) ||
                    record.employeeId?.employeeId?.toLowerCase().includes(term) ||
                    record.status?.toLowerCase().includes(term);
                  
                  const matchType = typeFilter === 'All Types' || record.attendanceType === typeFilter;
                  const matchStatus = statusFilter === 'All Status' || record.status === statusFilter;
                  
                  let matchDate = true;
                  if (dateFilter) {
                    const recordDate = new Date(record.date);
                    const localDateStr = `${recordDate.getFullYear()}-${String(recordDate.getMonth() + 1).padStart(2, '0')}-${String(recordDate.getDate()).padStart(2, '0')}`;
                    const utcDateStr = record.date ? (typeof record.date === 'string' ? record.date : recordDate.toISOString()).substring(0, 10) : '';
                    matchDate = localDateStr === dateFilter || utcDateStr === dateFilter;
                  }
                  
                  return matchSearch && matchType && matchStatus && matchDate;
                }).map(record => (
                  <tr key={record._id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {record.employeeId?.firstName} {record.employeeId?.lastName}
                      <div className="text-xs text-gray-500">{record.employeeId?.employeeId}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(record.date).toLocaleDateString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {record.attendanceType === 'MEETING' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                          💼 MEETING
                        </span>
                      ) : (
                        record.attendanceType
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {record.status === 'HOLIDAY' ? (
                        <span className="text-xs font-semibold rounded-full px-2.5 py-1 bg-purple-100 text-purple-800">HOLIDAY</span>
                      ) : canChangeAttendanceStatus ? (
                        <select
                          value={record.status}
                          onChange={(e) => handleUpdateStatus(record._id, e.target.value)}
                          className={`text-xs font-semibold rounded-full px-2 py-1 border-0 cursor-pointer 
                            ${record.status === 'PRESENT' ? 'bg-green-100 text-green-800' : 
                              record.status === 'HALF_DAY' ? 'bg-yellow-100 text-yellow-800' : 
                              record.status === 'CLOCKED_IN' ? 'bg-blue-100 text-blue-800' : 
                              record.status === 'PENDING_APPROVAL' ? 'bg-amber-100 text-amber-800' : 
                              'bg-red-100 text-red-800'}`}
                        >
                          <option value="PRESENT">PRESENT</option>
                          <option value="HALF_DAY">HALF DAY</option>
                          <option value="ABSENT">ABSENT</option>
                          <option value="WFH">WFH</option>
                          <option value="LEAVE">LEAVE</option>
                          <option value="CLOCKED_IN">CLOCKED IN</option>
                          <option value="PENDING_APPROVAL">PENDING APPROVAL</option>
                          <option value="MISSING_CLOCK_OUT">MISSING CLOCK OUT</option>
                        </select>
                      ) : (
                        <span className={`text-xs font-semibold rounded-full px-2.5 py-1 inline-block
                          ${record.status === 'PRESENT' ? 'bg-green-100 text-green-800' : 
                            record.status === 'HALF_DAY' ? 'bg-yellow-100 text-yellow-800' : 
                            record.status === 'CLOCKED_IN' ? 'bg-blue-100 text-blue-800' : 
                            record.status === 'PENDING_APPROVAL' ? 'bg-amber-100 text-amber-800' : 
                            'bg-red-100 text-red-800'}`}>
                          {record.status === 'PENDING_APPROVAL' ? 'PENDING APPROVAL' : record.status.replace('_', ' ')}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <EditableTimeCell 
                        record={record} 
                        field="clockIn" 
                        hasEditAccess={hasEditAccess} 
                        onUpdate={handleUpdateTime} 
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <EditableTimeCell 
                        record={record} 
                        field="clockOut" 
                        hasEditAccess={hasEditAccess} 
                        onUpdate={handleUpdateTime} 
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {formatWorkingHours(record)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-500 font-medium">
                      {formatDuration(record.lateMinutes)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-500 font-medium">
                      {formatDuration(record.earlyLeavingMinutes)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {record.overtimeMinutes > 0 ? (
                        <span className="text-green-600 font-medium">+{formatDuration(record.overtimeMinutes)}</span>
                      ) : null}
                      {record.overtimeDeductedMinutes > 0 ? (
                        <span className="text-amber-600 text-xs block font-medium">(-{record.overtimeDeductedMinutes}m used)</span>
                      ) : null}
                      {!record.overtimeMinutes && !record.overtimeDeductedMinutes && (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex flex-col gap-1 items-start">
                        {record.workReport && (
                          <button 
                            onClick={() => setSelectedReport({ title: 'Work Report', content: record.workReport })}
                            className="text-purple-600 hover:text-purple-900 underline text-xs font-medium"
                          >
                            Report
                          </button>
                        )}
                        {record.meetingReason && (
                          <button 
                            onClick={() => setSelectedReport({ title: 'Company Meeting Details', content: record.meetingReason })}
                            className="text-indigo-600 hover:text-indigo-900 underline text-xs font-medium"
                          >
                            💼 Meeting Note
                          </button>
                        )}
                        {!record.workReport && !record.meetingReason && (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex space-x-2">
                        {record.livePhotoUrl ? (
                          <a href={record.livePhotoUrl} title="Clock In Photo" target="_blank" rel="noopener noreferrer">
                            <img 
                              src={record.livePhotoUrl} 
                              alt="Clock-in" 
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              className="w-10 h-10 object-cover rounded shadow-sm border border-gray-200 hover:opacity-80 transition-opacity"
                            />
                          </a>
                        ) : null}
                        {record.clockOutPhotoUrl ? (
                          <a href={record.clockOutPhotoUrl} title="Clock Out Photo" target="_blank" rel="noopener noreferrer">
                            <img 
                              src={record.clockOutPhotoUrl} 
                              alt="Clock-out" 
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              className="w-10 h-10 object-cover rounded shadow-sm border border-gray-200 hover:opacity-80 transition-opacity"
                            />
                          </a>
                        ) : null}
                        {!record.livePhotoUrl && !record.clockOutPhotoUrl && (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>
        )
      ) : activeTab === 'HOLIDAYS' ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50/80">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Holiday Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {holidaysLoading ? (
                  <tr><td colSpan="3" className="px-6 py-4 text-center">Loading...</td></tr>
                ) : (() => {
                  const filteredHolidays = holidays.filter(h => 
                    !searchTerm || h.name.toLowerCase().includes(searchTerm.toLowerCase())
                  );
                  return filteredHolidays.length === 0 ? (
                    <tr><td colSpan="3" className="px-6 py-4 text-center">No holidays found</td></tr>
                  ) : (
                    filteredHolidays.map(holiday => (
                      <tr key={holiday._id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{holiday.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(holiday.date).toLocaleDateString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-xs font-semibold rounded-full px-2 py-1 bg-purple-100 text-purple-800">HOLIDAY</span>
                        </td>
                      </tr>
                    ))
                  );
                })()}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {activeTab === 'WFH' && (
            <div className="p-3 bg-gradient-to-r from-indigo-50/60 to-purple-50/60 border-b border-indigo-100 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-indigo-950">
                  WFH Quick Actions:
                </span>
                <span className="text-xs text-indigo-700">
                  {selectedWfhIds.length} of {wfhRequests.filter(r => (r.adminStatus || r.hrStatus) === 'PENDING').length} pending selected
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={bulkProcessing || selectedWfhIds.length === 0}
                  onClick={() => handleBulkUpdateWfhStatus('APPROVED')}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded-xl text-xs font-bold shadow-xs transition-colors flex items-center gap-1.5 cursor-pointer"
                >
                  <Check className="w-3.5 h-3.5" />
                  <span>Approve Selected ({selectedWfhIds.length})</span>
                </button>
                <button
                  type="button"
                  disabled={bulkProcessing || selectedWfhIds.length === 0}
                  onClick={() => handleBulkUpdateWfhStatus('REJECTED')}
                  className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white rounded-xl text-xs font-bold shadow-xs transition-colors flex items-center gap-1.5 cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                  <span>Reject Selected ({selectedWfhIds.length})</span>
                </button>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50/80">
              <tr>
                {activeTab === 'WFH' && (
                  <th className="px-4 py-3 text-center w-10">
                    <input
                      type="checkbox"
                      aria-label="Select all pending WFH"
                      checked={(() => {
                        const pendings = wfhRequests.filter(r => (r.adminStatus || r.hrStatus) === 'PENDING');
                        return pendings.length > 0 && pendings.every(r => selectedWfhIds.includes(r._id));
                      })()}
                      onChange={(e) => {
                        const pendings = wfhRequests.filter(r => (r.adminStatus || r.hrStatus) === 'PENDING');
                        if (e.target.checked) {
                          setSelectedWfhIds(pendings.map(r => r._id));
                        } else {
                          setSelectedWfhIds([]);
                        }
                      }}
                      className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500 cursor-pointer"
                    />
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">HR/Admin Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Manager Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Final Status</th>
                {activeTab === 'WFH' && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Status</th>}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(activeTab === 'WFH' ? wfhLoading : odLoading) ? (
                <tr><td colSpan={activeTab === 'WFH' ? "9" : "7"} className="px-6 py-4 text-center">Loading...</td></tr>
              ) : (activeTab === 'WFH' ? wfhRequests : odRequests).filter(req => {
                  const term = searchTerm.toLowerCase();
                  const matchSearch = req.employeeId?.firstName?.toLowerCase().includes(term) ||
                    req.employeeId?.lastName?.toLowerCase().includes(term) ||
                    req.employeeId?.employeeId?.toLowerCase().includes(term) ||
                    req.status?.toLowerCase().includes(term);
                    
                  const matchStatus = statusFilter === 'All Status' || req.status === statusFilter;
                  return matchSearch && matchStatus;
                }).length === 0 ? (
                <tr><td colSpan={activeTab === 'WFH' ? "9" : "7"} className="px-6 py-4 text-center">No {activeTab} requests found</td></tr>
              ) : (
                (activeTab === 'WFH' ? wfhRequests : odRequests).filter(req => {
                  const term = searchTerm.toLowerCase();
                  const matchSearch = req.employeeId?.firstName?.toLowerCase().includes(term) ||
                    req.employeeId?.lastName?.toLowerCase().includes(term) ||
                    req.employeeId?.employeeId?.toLowerCase().includes(term) ||
                    req.status?.toLowerCase().includes(term);
                    
                  const matchStatus = statusFilter === 'All Status' || req.status === statusFilter;
                  return matchSearch && matchStatus;
                }).map(req => {
                  const isPendingAdmin = (req.adminStatus || req.hrStatus) === 'PENDING';
                  const isChecked = selectedWfhIds.includes(req._id);
                  return (
                  <tr key={req._id} className={isChecked ? 'bg-indigo-50/40' : ''}>
                    {activeTab === 'WFH' && (
                      <td className="px-4 py-4 text-center">
                        {isPendingAdmin ? (
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {
                              setSelectedWfhIds(prev => 
                                prev.includes(req._id) ? prev.filter(x => x !== req._id) : [...prev, req._id]
                              );
                            }}
                            className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500 cursor-pointer"
                          />
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {req.employeeId?.firstName} {req.employeeId?.lastName}
                      <div className="text-xs text-gray-500">{req.employeeId?.employeeId}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {activeTab === 'WFH' ? (
                        <>
                          {new Date(req.startDate).toLocaleDateString()}
                          {req.startDate !== req.endDate && ` - ${new Date(req.endDate).toLocaleDateString()}`}
                        </>
                      ) : (
                        new Date(req.date).toLocaleDateString()
                      )}
                    </td>
                    <td 
                      className="px-6 py-4 text-sm text-gray-700 max-w-xs truncate cursor-pointer hover:text-blue-600 transition-colors"
                      title="Click to view full reason"
                      onClick={() => {
                        setSelectedRequest(req);
                        setDetailsModalOpen(true);
                      }}
                    >
                      {req.attendanceId && (
                        <span className="inline-block px-2 py-0.5 mb-1 text-[11px] font-semibold rounded bg-indigo-50 text-indigo-700 border border-indigo-200 mr-1.5">
                          💼 Meeting Clock-In
                        </span>
                      )}
                      <div className="flex items-center gap-1.5">
                        <span className="truncate">{req.reason}</span>
                        <span className="text-[10px] bg-blue-50 text-blue-600 font-semibold px-1.5 py-0.5 rounded shrink-0">View</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${(req.adminStatus || req.hrStatus) === 'APPROVED' ? 'bg-green-100 text-green-800' : 
                          (req.adminStatus || req.hrStatus) === 'REJECTED' ? 'bg-red-100 text-red-800' : 
                          'bg-yellow-100 text-yellow-800'}`}>
                        {(req.adminStatus || req.hrStatus) || 'PENDING'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${req.managerStatus === 'APPROVED' ? 'bg-green-100 text-green-800' : 
                          req.managerStatus === 'REJECTED' ? 'bg-red-100 text-red-800' : 
                          'bg-yellow-100 text-yellow-800'}`}>
                        {req.managerStatus || 'PENDING'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${req.status === 'APPROVED' ? 'bg-green-100 text-green-800' : 
                          req.status === 'REJECTED' ? 'bg-red-100 text-red-800' : 
                          'bg-yellow-100 text-yellow-800'}`}>
                        {req.status}
                      </span>
                    </td>
                    {activeTab === 'WFH' && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {req.paymentStatus && req.paymentStatus !== 'PENDING' ? (
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                            ${req.paymentStatus === 'FULL_DAY_PAID' ? 'bg-indigo-100 text-indigo-800' : 
                              req.paymentStatus === 'HALF_DAY_PAID' ? 'bg-blue-100 text-blue-800' : 
                              'bg-gray-100 text-gray-800'}`}>
                            {req.paymentStatus.replace(/_/g, ' ')}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs italic">Pending</span>
                        )}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex items-center gap-2">
                      <button
                        onClick={() => {
                          setCurrentHistory(req.history || []);
                          setHistoryModalOpen(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 font-normal text-xs underline"
                      >
                        Track
                      </button>
                      {(req.adminStatus || req.hrStatus) === 'PENDING' ? (
                        <>
                          <button onClick={() => activeTab === 'WFH' ? handleWfhUpdateStatus(req._id, 'APPROVED') : handleOdUpdateStatus(req._id, 'APPROVED')} className="text-green-600 hover:text-green-900">Approve</button>
                          <button onClick={() => activeTab === 'WFH' ? handleWfhUpdateStatus(req._id, 'REJECTED') : handleOdUpdateStatus(req._id, 'REJECTED')} className="text-red-600 hover:text-red-900">Reject</button>
                        </>
                      ) : (
                        <span className="text-gray-400 text-xs italic">Decided</span>
                      )}
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-screen overflow-y-auto">
            <h3 className="text-lg font-bold mb-4 text-gray-900">
              {typeof selectedReport === 'object' ? selectedReport.title : 'Work Report'}
            </h3>
            <p className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed">
              {typeof selectedReport === 'object' ? selectedReport.content : selectedReport}
            </p>
            <div className="mt-6 text-right">
              <button 
                onClick={() => setSelectedReport(null)}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {employeeDetailModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col shadow-2xl border border-gray-100 overflow-hidden">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-100 flex items-start justify-between bg-gradient-to-r from-gray-50 to-white">
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-bold text-gray-900">
                    {employeeDetailModal.employee.name}
                  </h3>
                  <span className="text-xs px-2.5 py-0.5 rounded-full font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                    {employeeDetailModal.employee.employeeId || employeeDetailModal.employee.role}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Monthly Attendance breakdown for{' '}
                  <span className="font-semibold text-gray-700">
                    {new Date(`${selectedMonth}-01`).toLocaleString('default', { month: 'long', year: 'numeric' })}
                  </span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEmployeeDetailModal(null)}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Metric KPI cards */}
            <div className="p-6 border-b border-gray-100 grid grid-cols-2 sm:grid-cols-5 gap-3 bg-gray-50/50">
              <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm text-center">
                <span className="text-[11px] font-semibold text-gray-500 uppercase block">Present</span>
                <span className="text-lg font-bold text-green-600">{employeeDetailModal.presentCount}</span>
              </div>
              <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm text-center">
                <span className="text-[11px] font-semibold text-gray-500 uppercase block">Half Day</span>
                <span className="text-lg font-bold text-yellow-600">{employeeDetailModal.halfDayCount}</span>
              </div>
              <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm text-center">
                <span className="text-[11px] font-semibold text-gray-500 uppercase block">Absent</span>
                <span className="text-lg font-bold text-red-600">{employeeDetailModal.absentCount}</span>
              </div>
              <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm text-center">
                <span className="text-[11px] font-semibold text-gray-500 uppercase block">Leave</span>
                <span className="text-lg font-bold text-amber-600">{employeeDetailModal.leaveCount}</span>
                <div className="flex items-center justify-center gap-1.5 mt-0.5 text-[10px]">
                  <span className="px-1.5 py-0.2 rounded font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200" title="Approved Paid Leaves">
                    {employeeDetailModal.paidLeaveCount || 0} Paid
                  </span>
                  <span className="px-1.5 py-0.2 rounded font-semibold bg-rose-50 text-rose-700 border border-rose-200" title="Approved Unpaid Leaves">
                    {employeeDetailModal.unpaidLeaveCount || 0} Unpaid
                  </span>
                </div>
              </div>
              <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm text-center">
                <span className="text-[11px] font-semibold text-gray-500 uppercase block">Total Hours</span>
                <span className="text-lg font-bold text-gray-800">{formatDuration(employeeDetailModal.totalWorkingMinutes)}</span>
              </div>
            </div>

            {/* Detailed records list for the employee */}
            <div className="p-6 overflow-y-auto flex-1">
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Daily Attendance Records ({employeeDetailModal.records.length})</h4>
              {employeeDetailModal.records.length === 0 ? (
                <div className="text-center py-10 text-gray-400 text-sm">No individual attendance records found for this month.</div>
              ) : (
                <div className="overflow-x-auto border border-gray-100 rounded-xl shadow-sm">
                  <table className="min-w-full divide-y divide-gray-100 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Clock In</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Clock Out</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Working Hours</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Late / Early</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Overtime</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {employeeDetailModal.records
                        .slice()
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .map(rec => (
                          <tr key={rec._id} className="hover:bg-gray-50/50">
                            <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">
                              {new Date(rec.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                              {rec.attendanceType || 'OFFICE'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center gap-1.5">
                                {canChangeAttendanceStatus ? (
                                  <select
                                    value={rec.status}
                                    onChange={async (e) => {
                                      const nextStatus = e.target.value;
                                      const extraData = rec.isSynthetic ? {
                                        employeeId: employeeDetailModal.employee._id,
                                        date: rec.date,
                                        attendanceType: rec.attendanceType || 'OFFICE'
                                      } : {};
                                      await handleUpdateStatus(rec._id, nextStatus, extraData);
                                      // Update local modal state immediately
                                      setEmployeeDetailModal(prev => {
                                        if (!prev) return null;
                                        const updatedRecords = prev.records.map(r => r._id === rec._id ? { ...r, status: nextStatus } : r);
                                        return { ...prev, records: updatedRecords };
                                      });
                                    }}
                                    className={`text-xs font-semibold rounded-full px-2 py-1 border-0 cursor-pointer 
                                      ${rec.status === 'PRESENT' ? 'bg-green-100 text-green-800' : 
                                        rec.status === 'HALF_DAY' ? 'bg-yellow-100 text-yellow-800' : 
                                        rec.status === 'CLOCKED_IN' ? 'bg-blue-100 text-blue-800' : 
                                        rec.status === 'PENDING_APPROVAL' ? 'bg-amber-100 text-amber-800' : 
                                        rec.status === 'LEAVE' ? 'bg-amber-100 text-amber-800' :
                                        'bg-red-100 text-red-800'}`}
                                  >
                                    <option value="PRESENT">PRESENT</option>
                                    <option value="HALF_DAY">HALF DAY</option>
                                    <option value="ABSENT">ABSENT</option>
                                    <option value="WFH">WFH</option>
                                    <option value="LEAVE">LEAVE</option>
                                    <option value="CLOCKED_IN">CLOCKED IN</option>
                                    <option value="PENDING_APPROVAL">PENDING APPROVAL</option>
                                  </select>
                                ) : (
                                  <span className={`text-xs font-semibold rounded-full px-2.5 py-1 inline-block
                                    ${rec.status === 'PRESENT' ? 'bg-green-100 text-green-800' : 
                                      rec.status === 'HALF_DAY' ? 'bg-yellow-100 text-yellow-800' : 
                                      rec.status === 'CLOCKED_IN' ? 'bg-blue-100 text-blue-800' : 
                                      rec.status === 'PENDING_APPROVAL' ? 'bg-amber-100 text-amber-800' : 
                                      rec.status === 'LEAVE' ? 'bg-amber-100 text-amber-800' :
                                      'bg-red-100 text-red-800'}`}>
                                    {rec.status.replace('_', ' ')}
                                  </span>
                                )}
                                {rec.status === 'LEAVE' && (
                                  rec.isPaidLeave ? (
                                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200" title="Approved Paid Leave">
                                      PAID
                                    </span>
                                  ) : (
                                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-200" title="Approved Unpaid Leave">
                                      UNPAID
                                    </span>
                                  )
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                              <EditableTimeCell 
                                record={rec} 
                                field="clockIn" 
                                hasEditAccess={hasEditAccess} 
                                onUpdate={async (r, f, v) => {
                                  await handleUpdateTime(r, f, v);
                                  await fetchHistory();
                                }} 
                              />
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                              <EditableTimeCell 
                                record={rec} 
                                field="clockOut" 
                                hasEditAccess={hasEditAccess} 
                                onUpdate={async (r, f, v) => {
                                  await handleUpdateTime(r, f, v);
                                  await fetchHistory();
                                }} 
                              />
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs">
                              {formatWorkingHours(rec)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                              {rec.lateMinutes > 0 && <span className="text-red-500 font-medium">Late: {formatDuration(rec.lateMinutes)} </span>}
                              {rec.earlyLeavingMinutes > 0 && <span className="text-red-500 font-medium">Early: {formatDuration(rec.earlyLeavingMinutes)}</span>}
                              {!rec.lateMinutes && !rec.earlyLeavingMinutes && <span className="text-gray-400">-</span>}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs font-medium text-green-600">
                              {rec.overtimeMinutes > 0 ? `+${formatDuration(rec.overtimeMinutes)}` : <span className="text-gray-400 font-normal">-</span>}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-100 bg-gray-50/50 flex justify-end">
              <button
                type="button"
                onClick={() => setEmployeeDetailModal(null)}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 shadow-sm transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAttendance;
